import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Alert, App, Button, Drawer, Input, Modal, Popconfirm, Select } from 'antd'
import { DRAWER_SIZE } from '@/const/drawer.ts'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { useTRPC } from '@/trpc/react.ts'
import { BillDetail } from './bill-detail.tsx'
import { GroupCollectionSettings } from './group-collection-settings.tsx'
import { GroupDetailContent } from './group-detail-content.tsx'
import { GroupDraftEditor } from './group-draft-editor.tsx'
import { GroupMoneyEntry } from './group-money-entry.tsx'
import { GroupRevisionEditor } from './group-revision-editor.tsx'
import { GroupRevisionPreview } from './group-revision-preview.tsx'
import { snapshotObject } from './utils/finance-snapshot.ts'
import { toCents } from './utils/money.ts'
import { refreshMoneyQueries } from './utils/refresh-money-queries.ts'

export function GroupDetail({
  id,
  onClose,
  canManage,
  initialAction
}: {
  id: number
  onClose: () => void
  canManage: boolean
  initialAction?: 'void' | 'edit' | 'issue' | 'collection'
}) {
  const trpc = useTRPC(),
    cache = useQueryClient(),
    { message } = App.useApp(),
    q = useQuery(trpc.bill.groupDetail.queryOptions({ groupId: id }))
  const issue = useMutation(trpc.bill.issueGroup.mutationOptions()),
    voidGroup = useMutation(trpc.bill.voidGroup.mutationOptions())
  const history = useQuery(trpc.bill.groupRevisions.queryOptions({ groupId: id }))
  const previousRevisions = history.data?.filter((item) => item.revisionNumber < (q.data?.revisionNumber ?? 0)) ?? []
  const [collectionSettings, setCollectionSettings] = useState(initialAction === 'collection')
  const [revising, setRevising] = useState(false)
  const [revision, setRevision] = useState<number>()
  const historical = useQuery({
    ...trpc.bill.groupDetail.queryOptions({ groupId: id, revisionNumber: revision }),
    enabled: !!revision
  })
  const [open, setOpen] = useState(true)
  const [issuing, setIssuing] = useState(initialAction === 'issue')
  const [editing, setEditing] = useState(initialAction === 'edit')
  const [receipting, setReceipting] = useState(false)
  const [voiding, setVoiding] = useState(initialAction === 'void'),
    [reason, setReason] = useState(''),
    [billId, setBillId] = useState<number>()
  const printRef = useRef<HTMLDivElement>(null)
  const g = q.data,
    account = snapshotObject(g?.accountSnapshot)
  const draftAccounts = useQuery({
    ...trpc.bill.accounts.queryOptions({ organizationId: g?.organizationId ?? 1, purpose: 'bill.group.read' }),
    enabled: g?.status === 'draft'
  })
  const displayAccount =
    g?.status === 'draft' ? draftAccounts.data?.find((a) => a.id === g.collectionAccountId) : account
  const refresh = async () => {
    await refreshMoneyQueries(cache, trpc)
  }
  return (
    <>
      {((initialAction !== 'collection' && initialAction !== 'edit') || q.isError) && (
        <Drawer
          open={open}
          size={DRAWER_SIZE}
          title={'收款账单组 · ' + (g?.groupNo ?? '读取中')}
          closable={{ placement: 'end' }}
          onClose={() => setOpen(false)}
          afterOpenChange={(visible) => {
            if (!visible) {
              onClose()
            }
          }}
          classNames={{ body: 'p-0!' }}
          loading={q.isPending}
          footer={
            g && (
              <div className="flex min-h-10 flex-wrap items-center justify-end gap-3">
                {!revision && canManage && g.status !== 'voided' && (
                  <Button size="medium" onClick={() => setCollectionSettings(true)}>
                    归属月份 / 催收设置
                  </Button>
                )}
                {!revision && canManage && g.status !== 'voided' && (
                  <Authorized permissions={['bill.group.void']}>
                    <Button size="medium" danger onClick={() => setVoiding(true)}>
                      作废并释放账单
                    </Button>
                  </Authorized>
                )}
                {g.status !== 'draft' && (
                  <Link
                    to="/finance/receipts"
                    search={{ ids: [id], revisionNumber: revision }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button size="medium">打印收款通知单</Button>
                  </Link>
                )}
                {!revision && canManage && g.status === 'issued' && (
                  <Button size="medium" onClick={() => setRevising(true)}>
                    调整组内账单
                  </Button>
                )}
                {!revision && canManage && g.status === 'draft' && (
                  <Button size="medium" onClick={() => setEditing(true)}>
                    调整组内账单
                  </Button>
                )}
                {!revision && canManage && g.status === 'draft' && (
                  <Popconfirm
                    open={issuing}
                    onOpenChange={setIssuing}
                    title="确认出单并冻结当前快照？"
                    onConfirm={async () => {
                      try {
                        await issue.mutateAsync({
                          groupId: id,
                          expectedUpdatedAt: g.updatedAt,
                          expectedOutstandingAmount: g.outstandingAmount
                        })
                        await refresh()
                      } catch (e) {
                        void message.error(e instanceof Error ? e.message : '出单失败')
                      }
                    }}
                  >
                    <Authorized permissions={['bill.group.issue']}>
                      <Button size="medium" type="primary" loading={issue.isPending}>
                        确认出单
                      </Button>
                    </Authorized>
                  </Popconfirm>
                )}

                {!revision && canManage && g.status === 'issued' && toCents(g.outstandingAmount) > BigInt(0) && (
                  <Authorized permissions={['bill.receipt.record']}>
                    <Button size="medium" type="primary" onClick={() => setReceipting(true)}>
                      登记收款
                    </Button>
                  </Authorized>
                )}
              </div>
            )
          }
        >
          {q.isError && <Alert type="error" title={q.error.message} />}
          {g && (!!g.revisionDraft || !!previousRevisions.length || !!revision) && (
            <div className="space-y-3 px-6 pt-3">
              {!revision && !!g.revisionDraft && (
                <GroupRevisionPreview group={g} canManage={canManage} refresh={refresh} />
              )}
              {historical.isError && revision && <Alert type="error" title={historical.error.message} />}
              {(!!previousRevisions.length || !!revision) && (
                <Select
                  size="small"
                  aria-label="收款单历史版本"
                  className="w-64 max-w-full"
                  value={revision}
                  placeholder="查看历史版本"
                  allowClear
                  onChange={setRevision}
                  options={previousRevisions.map((r) => ({
                    value: r.revisionNumber,
                    label: `版本 ${r.revisionNumber} · ${r.reason}`
                  }))}
                />
              )}
              {revision && (
                <Link to="/finance/receipts" search={{ ids: [id], revisionNumber: revision }} target="_blank">
                  <Button>导出此历史版本</Button>
                </Link>
              )}
            </div>
          )}
          {revision && historical.isPending && <Alert type="info" title="正在读取历史快照" />}
          {g && (!revision || historical.data) && (
            <GroupDetailContent
              group={revision ? historical.data! : g}
              historical={!!revision}
              displayAccount={
                revision && historical.data ? snapshotObject(historical.data.accountSnapshot) : displayAccount
              }
              printRef={printRef}
              onBill={setBillId}
            />
          )}
          {/* 保持详情处于父抽屉上下文中，复用 Drawer 的多层推开与回退动画。 */}
          {billId && (
            <BillDetail
              key={billId}
              billId={billId}
              canManage={!revision && canManage}
              onClose={() => setBillId(undefined)}
            />
          )}
        </Drawer>
      )}
      <Modal
        open={voiding}
        title="作废收款账单组"
        onCancel={() => setVoiding(false)}
        confirmLoading={voidGroup.isPending}
        okButtonProps={{ danger: true, disabled: !reason.trim() }}
        onOk={async () => {
          try {
            await voidGroup.mutateAsync({ groupId: id, reason })
            await refresh()
            setVoiding(false)
          } catch (e) {
            void message.error(e instanceof Error ? e.message : '作废失败')
          }
        }}
      >
        <Input.TextArea
          aria-label="作废原因"
          placeholder="填写作废原因"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Modal>
      {collectionSettings && g && (
        <GroupCollectionSettings
          group={g}
          onClose={() => {
            if (initialAction === 'collection') {
              onClose()
              return
            }
            setCollectionSettings(false)
          }}
          onSaved={async () => {
            await refreshMoneyQueries(cache, trpc)
          }}
        />
      )}
      {revising && g && <GroupRevisionEditor group={g} onClose={() => setRevising(false)} onSaved={refresh} />}
      {editing && g && (
        <GroupDraftEditor
          group={g}
          onClose={() => {
            if (initialAction === 'edit') {
              onClose()
              return
            }
            setEditing(false)
          }}
          onSaved={refresh}
        />
      )}
      {receipting && g && <GroupMoneyEntry key={id} groupId={id} onClose={() => setReceipting(false)} />}
    </>
  )
}
